import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ChevronLeft, Euro, Percent, Loader2, Save, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCommission() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [commissionRate, setCommissionRate] = useState(15);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    const fetchCommission = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/commission`, {
          headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` },
        });
        if (response.ok) {
          const data = await response.json();
          setCommissionRate((data.commission_rate * 100));
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCommission();
  }, [user, token, navigate]);

  const handleSave = async () => {
    const rate = parseFloat(commissionRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('Le taux doit être entre 0 et 100%');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/commission`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ commission_rate: rate / 100 }),
      });
      if (response.ok) {
        toast.success('Taux de commission mis à jour');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Example calculation
  const exampleRate = 50;
  const commission = exampleRate * (commissionRate / 100);
  const clientPrice = exampleRate + commission;

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="admin-commission-page">
      <div className="container-app max-w-2xl">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/admin')}
          data-testid="back-to-admin"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Retour au tableau de bord
        </Button>

        <h1 className="font-heading text-3xl font-semibold mb-2">
          Gestion de la Commission
        </h1>
        <p className="text-muted-foreground mb-8">
          Configurez le taux de commission appliqué sur toutes les réservations
        </p>

        <Card className="dashboard-card mb-8">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Percent className="w-5 h-5" />
              Taux de Commission Global
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="mb-2 block text-base font-medium">Taux de commission (%)</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  className="w-32 text-lg font-semibold text-center"
                  data-testid="commission-rate-input"
                />
                <span className="text-xl font-semibold text-muted-foreground">%</span>
              </div>
            </div>

            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full sm:w-auto"
              data-testid="save-commission-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Info className="w-5 h-5" />
              Comment ça fonctionne
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium">Exemple avec un tarif prestataire de {exampleRate}€/h :</p>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tarif prestataire</span>
                <span>{exampleRate.toFixed(2)}€/h</span>
              </div>
              <div className="flex justify-between text-sm text-accent">
                <span>Commission ({parseFloat(commissionRate).toFixed(0)}%)</span>
                <span>+{commission.toFixed(2)}€</span>
              </div>
              <Separator />
              <div className="flex justify-between font-bold">
                <span>Prix visible par le client</span>
                <span data-testid="example-client-price">{clientPrice.toFixed(2)}€/h</span>
              </div>
            </div>
            
            <div className="text-sm text-muted-foreground space-y-2">
              <p>- Le <strong>client</strong> voit un tarif unique (commission incluse)</p>
              <p>- Le <strong>prestataire</strong> voit son tarif de base</p>
              <p>- Le détail de la commission apparaît uniquement sur la <strong>facture</strong></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
